import React, { ReactNode } from 'react';
import useUserAccount from '../../hooks/stateHooks/userAccountHook';
import DashboardHeader from '../../components/dashboard/tabs/DashboardHeader';
import GTable from '../../components/elements/GTable';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import PopOnMe from '../../components/elements/poponme';
import { getNameInitials, getPhoneNumberFromShortCodeFormat } from '../../utilities/miscFunctions';
import { approveReconciliationBatch } from '../../api/requests';
import { ITableRowAction } from '../../components/elements/GTable/GTable';
import clsx from 'clsx';
import useModal from '../../hooks/modalHook';
import RoleBadge from '../../components/elements/RoleBage';
import { toast } from 'react-toastify';
import useSearchQuery from '../../hooks/SearchQueryHook';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import Avatar from '../../components/elements/Avatar';
import useTransactions from '../../hooks/transactionsHook';
import { Reconciliation } from '../../resources/types';
import CediSign from '../../components/elements/CediSign';
import moment from 'moment';
import ActionConfirmation from '../../components/elements/DeleteConfirmation';
import { RECONCILIATION_STAGES } from '../../resources/enums';
import StatusBadge from '../../components/elements/StatusBadge';
import Loader from '../../components/Loader';

interface IProps {
  id?: string;
  showHeader?: boolean;
  showSearch?: boolean;
  status?: RECONCILIATION_STAGES
}

function ReconciliationsList(props: IProps) {
  const { getProfile, user } = useUserAccount();
  const { ModalComponent, updateModalStates, closeModal } = useModal();

  const { fetchReconciliations, reconciliations } = useTransactions({ onTransactionAdded: () => { } })
  const { debouncedQuery } = useSearchQuery();
  const showHeader = props.showHeader ?? true;
  const showSearch = props.showSearch ?? true;

  const userId = props.id ?? getProfile().id;

  const { data, isLoading, isStale, isFetching } = useQuery<any, Error>({
    queryKey: [
      'users',
      debouncedQuery,
      userId,
    ],
    queryFn: () => fetchReconciliations({
      query: debouncedQuery,
      agentId: userId,
      status: props.status
    })
  });

  const getTableRowActions = (data: Reconciliation): ITableRowAction[] => {
    const actions = [];

    if ((user.isOwner() || user.isAdmin()) && data.status === RECONCILIATION_STAGES.PENDING) {
      actions.push({
        label: 'Approve',
        callback: (data: Reconciliation) => {
          approveReconciliation(data.id);
        },
        data: data
      })
    }

    return actions;
  };

  const approveReconciliation = (id: string) => {
    updateModalStates({
      show: true,
      children: <ActionConfirmation
        onClose={() => {
          closeModal();
        }}
        onConfirm={() => {
          approveReconciliationBatch(id)
            .then((response: any) => {
              toast.success("Reconciliation approved successfully");
              fetchReconciliations({});
              closeModal();
            }).catch((error: any) => {
              toast.error("Failed to approve reconciliation. Try again later.");
              console.log("error", error);
            })
        }}
        confirmationMessage='Are you sure you want to approve this reconciliation. The action cannot be undone'
        title='Confirm Approval'
      />
    })


  }

  const columns = React.useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Submitted By',
            accessor: 'created_by',
            Cell: (row: any) => {
              return <Link to={`/admin/agents/${row.cell.value.id}`} className='text-link-main dark:text-link-dark flex items-center gap-2'>
                <Avatar
                  src={row.cell.value.profileImage}
                  size='xs'
                  nameInitials={getNameInitials(`${row?.cell?.value?.first_name} ${row?.cell?.value?.last_name}`)}
                />
                <span>{`${row?.cell?.value?.first_name} ${row?.cell?.value?.last_name}`}</span>
              </Link>;
            }
          },
          {
            Header: 'Amount',
            accessor: 'expected_amount',
            Cell: (row: any) => {
              return <span> <CediSign /> {row.cell.value ?? 'N/A'}</span>;
            }
          },
          {
            Header: 'Status',
            accessor: 'status',
            Cell: (row: any) => {
              return <StatusBadge status={row.cell.value} />;
            }
          },
          {
            Header: 'Received By',
            accessor: 'received_by',
            Cell: (row: any) => {
              return <span>{row.cell.value.first_name ?? ''} {row.cell.value.last_name ?? ''}</span>;
            }
          },
          {
            Header: 'Date',
            accessor: 'created_at',
            Cell: (row: any) => {
              return <span>{moment(row.cell.value).format("DD/MM/YYYY")}</span>;
            }
          },
          {
            id: 'Action',
            Cell: (row: any) => {
              return (
                <div className="cursor-pointer flex justify-center">
                  {getTableRowActions(row.row.original)?.length > 0 && (
                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-4 w-4" />
                    </PopOnMe>
                  )}
                </div>
              );
            }
          }
        ]
      }
    ],
    []
  );

  const getListItemElement = (item: Record<string, any>, accessor: string): ReactNode => {
    const value = item[accessor as keyof Reconciliation];

    switch (accessor) {
      case "created_by":
        return <div className="flex items-center gap-3">

          <Avatar
            src={value.profileImage}
            nameInitials={getNameInitials(`${value.first_name} ${value.last_name}`)}
            size={window.innerWidth >= 768 ? "md" : "sm"}
          />

          <div>
            <p className="font-medium text-gray-900">
              {`${value.first_name} ${value.last_name}`}
            </p>
          </div>
        </div>
      case "expected_amount":
        return <div className=""><CediSign /> {value}</div>

      case "created_at":
        return <div className="">{moment(value).format("Do MMM, YYYY")}</div>

      case "actions":
        return <div className="flex justify-end gap-2">
          {((user.isOwner() || user.isAdmin()) && data.status === RECONCILIATION_STAGES.PENDING) && <button
            className="bg-green-600 hover:bg-green-700 text-white text-sm px-4 py-2 rounded-lg transition"
            onClick={() => approveReconciliation(item.id)}
          >
            Approve
          </button>}


        </div>

      case "person.phone":
        return <span className="text-gray-600">{getPhoneNumberFromShortCodeFormat(item.person.phone)}</span>

      case "relationship_type":
        return <span className="text-gray-600"><RoleBadge role={value} /></span>

      default:
        return <span className="text-gray-600">{value as string}</span>
    }
  }

  if (isLoading) {
    return <Loader message='Loading Reconciliations...' />
  }

  return (
    <div>
      {showHeader && <DashboardHeader
        heading='Reconciliations'
        rightChildren={
          <div className='flex'>

          </div>}
      />}

      <div className='mt-4'>
        {/* {window.innerWidth > 768 ? ( */}
        <GTable
          selectionChanged={(selection: any) => {
            // setSlcMember(selection);
          }}
          columns={columns}
          data={reconciliations}
          tableType="reconciliations"
          // onSearch={(searchString: string) => setSearchString(searchString)}
          searchPlaceholder={'Search by name '}
          // paginationData={policiesPaginationData}
          // onPageChange={handlePageChange}
          // showPerPage={showPerPage}
          // onShowPerPageChange={(page: number) => setShowPerPage(page)}
          // onRowClick={(account: Account) => {
          //   navigate(
          //     `${getDashboardBase()}/policies/${policy.ref
          //     }?filter=${filter}&page=${currentPage}`,
          //     { replace: true }
          //   );
          // }}
          // topActions={getTableTopActions()}
          columnClasses={clsx('first:min-w-[210px] min-w-[10rem] last:min-w-[50px]')}
          // loading={loadingUsers}
          // propBasedColumnClasses={(policy: Policy) => checkComprehensive(policy)}
          tableContainerId="tableId"
          showSearch={showSearch}
        />
        {/* ) : (
          <List
            list={reconciliations}
            columns={["Submitted By", "Amount", "Status", "Date", "Actions"]}
            accessors={["created_by", "expected_amount", "status", "created_at", "actions"]}
            getListItemElement={getListItemElement}
          />
        )} */}

      </div>
      <ModalComponent />
    </div>
  );
}

export default ReconciliationsList;
